import type { Grade, Op } from "../types";

export const WALK_MS = 350;
export const MIN_DECISION_MS = 200;
export const GAME = "fact-trail";
export const DAILY_TAG = "fact-trail-v1";

export const GRADE_LABELS: Record<Grade, string> = {
  0: "K",
  1: "1",
  2: "2",
  3: "3",
  4: "4",
  5: "5",
  6: "6",
  7: "7",
  8: "8",
};

export const GRADE_SAMPLES: Record<Grade, string> = {
  0: "3 + 2",
  1: "9 + 7",
  2: "14 − 6",
  3: "7 × 8",
  4: "12 × 11",
  5: "3 × 2/5",
  6: "3/4 ÷ 1/4",
  7: "−3 + 8",
  8: "3 + 4 × 2",
};

export function bandFor(grade: Grade): "k2" | "g35" | "g68" {
  if (grade <= 2) return "k2";
  if (grade <= 5) return "g35";
  return "g68";
}

export function gridSize(grade: Grade): number {
  const b = bandFor(grade);
  return b === "k2" ? 5 : b === "g35" ? 6 : 7;
}

export function pathLength(grade: Grade): number {
  const b = bandFor(grade);
  return b === "k2" ? 8 : b === "g35" ? 10 : 12;
}

export function timeLimitMs(grade: Grade, extraTime: boolean, mode: string): number {
  const b = bandFor(grade);
  let sec = b === "k2" ? 90 : b === "g35" ? 75 : 60;
  if (extraTime && mode !== "daily") sec += 30;
  return sec * 1000;
}

export interface LevelArt {
  file: string;
  title: string;
  blurb: string;
}

export const LEVEL_ART: LevelArt[] = [
  { file: "01-meadow.jpg", title: "Sunny Meadow Picnic", blurb: "A blanket, a basket, and a winding path." },
  { file: "02-tidepool.jpg", title: "Tide-Pool Shoreline", blurb: "Starfish hide in the pools." },
  { file: "03-orchard.jpg", title: "Orange Tree Orchard", blurb: "Pip's favorite groves." },
  { file: "04-diamond.jpg", title: "Dusk Diamond", blurb: "Lanterns over the infield." },
  { file: "05-treehouse.jpg", title: "Treehouse Village", blurb: "Rope bridges in the oaks." },
  { file: "06-railroad.jpg", title: "Canyon Railroad", blurb: "A little train over the trestle." },
  { file: "07-reef.jpg", title: "Coral Reef", blurb: "Sunlight on the garden below." },
  { file: "08-observatory.jpg", title: "Mountain Observatory", blurb: "Stars above the pines." },
  { file: "09-library.jpg", title: "Autumn Library", blurb: "Leaves and warm windows." },
  { file: "10-lanterns.jpg", title: "Winter Lantern Festival", blurb: "Gold light on the snow." },
  { file: "11-ruins.jpg", title: "Desert Canyon Ruins", blurb: "An oasis among the arches." },
  { file: "12-clouds.jpg", title: "Cloud-Island Garden", blurb: "Bridges through the sky." },
];

export function artForLevel(level: number): LevelArt {
  return LEVEL_ART[(Math.max(1, level) - 1) % LEVEL_ART.length];
}

export function artForDay(dayIndex: number): LevelArt {
  return LEVEL_ART[Math.abs(dayIndex) % LEVEL_ART.length];
}

/** Campaign skill mix: 1–3 home, 4–7 mix previous, 8–9 hard new, 10 mixed. */
export type Skill =
  | "add5"
  | "sub5"
  | "add10"
  | "sub10"
  | "add20"
  | "sub20"
  | "doubles"
  | "make10"
  | "add2d"
  | "add2dR"
  | "sub2d"
  | "sub2dR"
  | "mul10"
  | "div10"
  | "add100"
  | "sub100"
  | "unitFrac"
  | "mul12"
  | "div12"
  | "mulMd"
  | "eqFrac"
  | "fracLike"
  | "mulBig"
  | "divShort"
  | "fracUnlike"
  | "fracTimesW"
  | "fracDiv"
  | "decAdd"
  | "ratio"
  | "intAdd"
  | "intMul"
  | "rational"
  | "order"
  | "exp"
  | "sqrt";

export function skillsForCampaign(grade: Grade, level: number): Skill[] {
  const L = Math.max(1, Math.min(10, level));
  const home: Record<Grade, Skill[]> = {
    0: ["add5", "sub5"],
    1: ["add10", "sub10", "doubles", "make10"],
    2: ["add20", "sub20"],
    3: ["mul10", "div10"],
    4: ["mul12", "div12"],
    5: ["mulBig", "divShort"],
    6: ["fracDiv", "decAdd"],
    7: ["intAdd", "intMul"],
    8: ["order", "exp"],
  };
  const hard: Record<Grade, Skill[]> = {
    0: ["add10", "sub10"],
    1: ["add20", "sub20"],
    2: ["add2dR", "sub2dR"],
    3: ["unitFrac", "add100"],
    4: ["mulMd", "eqFrac", "fracLike"],
    5: ["fracUnlike", "fracTimesW"],
    6: ["ratio", "fracDiv"],
    7: ["rational", "intAdd"],
    8: ["sqrt", "order", "exp"],
  };
  const prev: Record<Grade, Skill[]> = {
    0: ["add5", "sub5"],
    1: ["add5", "sub5", "add10", "sub10"],
    2: ["add10", "sub10", "add20", "doubles"],
    3: ["add20", "add2d", "mul10"],
    4: ["mul10", "div10", "add100"],
    5: ["mul12", "fracLike", "mulMd"],
    6: ["fracUnlike", "fracTimesW", "mulBig"],
    7: ["decAdd", "fracDiv", "ratio"],
    8: ["intAdd", "rational", "order"],
  };
  if (L <= 3) return home[grade];
  if (L <= 7) return [...home[grade], ...prev[grade]];
  if (L <= 9) return hard[grade];
  return [...home[grade], ...hard[grade], ...prev[grade]];
}

export function skillsForPractice(grade: Grade, op: Op): Skill[] {
  const byOp: Record<Op, Skill[]> = {
    add: ["add5", "add10", "add20", "add2d", "add2dR", "add100", "intAdd", "decAdd"],
    sub: ["sub5", "sub10", "sub20", "sub2d", "sub2dR", "sub100", "intAdd"],
    mul: ["mul10", "mul12", "mulMd", "mulBig", "intMul", "exp"],
    div: ["div10", "div12", "divShort", "fracDiv", "sqrt"],
    frac: ["unitFrac", "eqFrac", "fracLike", "fracUnlike", "fracTimesW", "fracDiv", "rational"],
    mixed: [],
  };
  const allowed = new Set<Skill>();
  for (let g = 0 as Grade; g <= grade; g = (g + 1) as Grade) {
    for (const s of skillsForCampaign(g as Grade, 10)) allowed.add(s);
    for (const s of skillsForCampaign(g as Grade, 2)) allowed.add(s);
    for (const s of skillsForCampaign(g as Grade, 8)) allowed.add(s);
  }
  if (op === "mixed") return [...allowed];
  const hit = byOp[op].filter((s) => allowed.has(s));
  return hit.length ? hit : [...allowed];
}

export const OP_LABELS: Record<Op, string> = {
  add: "Add",
  sub: "Subtract",
  mul: "Multiply",
  div: "Divide",
  frac: "Fractions",
  mixed: "Mixed",
};

export const DIRS: Dir[] = ["N", "E", "S", "W"];
export const DIR_DELTA: Record<Dir, { dr: number; dc: number }> = {
  N: { dr: -1, dc: 0 },
  E: { dr: 0, dc: 1 },
  S: { dr: 1, dc: 0 },
  W: { dr: 0, dc: -1 },
};

export function dirBetween(a: { r: number; c: number }, b: { r: number; c: number }): Dir | null {
  const dr = b.r - a.r;
  const dc = b.c - a.c;
  if (dr === -1 && dc === 0) return "N";
  if (dr === 1 && dc === 0) return "S";
  if (dr === 0 && dc === 1) return "E";
  if (dr === 0 && dc === -1) return "W";
  return null;
}
